import { useState, useEffect } from 'react';
import { LockIcon, UserIcon, EnvelopeIcon } from '@phosphor-icons/react';
import { supabase } from '@/lib/supabase';
import { EditAccountContent } from '@/content/EditAccountContent';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { updateUserProfile } from '@/lib/services/userProfiles';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Banner } from '@/components/Banner';

export function EditAccount() {
  const { user } = useAuth();
  const { profile, isLoading: isProfileLoading } = useUserProfile();

  // Password state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Name state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);

  // Email state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // Initialize form values from profile
  useEffect(() => {
    if (profile) {
      setName(profile.full_name || '');
      setEmail(profile.email || '');
    }
  }, [profile]);

  // Handle password update
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError(EditAccountContent.password.dialog.errors.mismatch);
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      setPasswordError(EditAccountContent.password.dialog.errors.tooShort);
      return;
    }

    setPasswordLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setPasswordError(updateError.message);
        setPasswordLoading(false);
        return;
      }

      // Success
      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordLoading(false);

      // Close dialog after 2 seconds
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch {
      setPasswordError(EditAccountContent.password.dialog.errors.unexpected);
      setPasswordLoading(false);
    }
  };

  // Reset password dialog state when it closes
  const handlePasswordDialogOpenChange = (open: boolean) => {
    setPasswordDialogOpen(open);
    if (!open) {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError(null);
      setPasswordSuccess(false);
    }
  };

  // Handle name update
  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    setNameSuccess(false);

    if (!name.trim()) {
      setNameError(EditAccountContent.name.errors.required);
      return;
    }

    setNameLoading(true);

    try {
      await updateUserProfile(user?.id, { full_name: name.trim() });
      setNameSuccess(true);
      setNameLoading(false);

      // Reset success message after 3 seconds
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (error) {
      setNameError(
        error instanceof Error
          ? error.message
          : EditAccountContent.name.errors.unexpected
      );
      setNameLoading(false);
    }
  };

  // Handle email update
  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailSuccess(false);

    if (!email.trim()) {
      setEmailError(EditAccountContent.email.errors.required);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailError(EditAccountContent.email.errors.invalid);
      return;
    }

    setEmailLoading(true);

    try {
      // Update email in auth (this will send a confirmation email)
      const { error: authError } = await supabase.auth.updateUser({
        email: email.trim(),
      });

      if (authError) {
        setEmailError(authError.message);
        setEmailLoading(false);
        return;
      }

      // Also update email in profile table
      await updateUserProfile(user?.id, { email: email.trim() });

      setEmailSuccess(true);
      setEmailLoading(false);

      // Reset success message after 5 seconds (longer for email confirmation notice)
      setTimeout(() => setEmailSuccess(false), 5000);
    } catch (error) {
      setEmailError(
        error instanceof Error
          ? error.message
          : EditAccountContent.email.errors.unexpected
      );
      setEmailLoading(false);
    }
  };

  const content = EditAccountContent;

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-8 mt-[34px]">
      <Header enableBack />
      <h1 className="font-field-74 text-h1 text-slate-50 mb-6">
        {content.title}
      </h1>

      {/* Password Section */}
      <div className="bg-slate-600 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <LockIcon size={24} weight="regular" className="text-slate-400" />
          <h2 className="text-h2 text-slate-50 font-field-64">
            {content.password.title}
          </h2>
        </div>
        <p className="text-body text-slate-300 mb-4">
          {content.password.description}
        </p>
        <Button
          variant="outline"
          size="lg"
          onClick={() => setPasswordDialogOpen(true)}
          className="w-full sm:w-auto"
        >
          {content.password.button}
        </Button>
      </div>

      {/* Name Section */}
      <div className="bg-slate-600 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <UserIcon size={24} weight="regular" className="text-slate-400" />
          <h2 className="text-h2 text-slate-50 font-field-64">
            {content.name.title}
          </h2>
        </div>
        <p className="text-body text-slate-300 mb-4">
          {content.name.description}
        </p>
        <form onSubmit={handleUpdateName} className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium mb-2 text-slate-200"
            >
              {content.name.label}
            </label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isProfileLoading || nameLoading}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-500 rounded-md text-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:opacity-50"
              placeholder={content.name.placeholder}
            />
          </div>

          {nameError && <Banner variant="error" description={nameError} />}

          {nameSuccess && (
            <Banner variant="success" description={content.name.success} />
          )}

          <Button
            size="lg"
            type="submit"
            disabled={isProfileLoading || nameLoading || nameSuccess}
            className="w-full sm:w-auto"
          >
            {nameLoading
              ? content.name.saving
              : nameSuccess
                ? content.name.saved
                : content.name.save}
          </Button>
        </form>
      </div>

      {/* Email Section */}
      <div className="bg-slate-600 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <EnvelopeIcon size={24} weight="regular" className="text-slate-400" />
          <h2 className="text-h2 text-slate-50 font-field-64">
            {content.email.title}
          </h2>
        </div>
        <p className="text-body text-slate-300 mb-4">
          {content.email.description}
        </p>
        <form onSubmit={handleUpdateEmail} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-2 text-slate-200"
            >
              {content.email.label}
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isProfileLoading || emailLoading}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-500 rounded-md text-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:opacity-50"
              placeholder={content.email.placeholder}
            />
          </div>

          {emailError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{emailError}</p>
            </div>
          )}

          {emailSuccess && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md">
              <p className="text-sm text-green-500">{content.email.success}</p>
            </div>
          )}

          <Button
            size="lg"
            type="submit"
            disabled={isProfileLoading || emailLoading || emailSuccess}
            className="w-full sm:w-auto"
          >
            {emailLoading
              ? content.email.saving
              : emailSuccess
                ? content.email.saved
                : content.email.save}
          </Button>
        </form>
      </div>

      {/* Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onOpenChange={handlePasswordDialogOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{content.password.dialog.title}</DialogTitle>
            <DialogDescription>
              {content.password.dialog.description}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium mb-2 text-foreground"
              >
                {content.password.dialog.newPasswordLabel}
              </label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
                className="w-full px-4 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={content.password.dialog.newPasswordPlaceholder}
                disabled={passwordLoading || passwordSuccess}
              />
            </div>
            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium mb-2 text-foreground"
              >
                {content.password.dialog.confirmPasswordLabel}
              </label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
                className="w-full px-4 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={content.password.dialog.confirmPasswordPlaceholder}
                disabled={passwordLoading || passwordSuccess}
              />
            </div>

            {passwordError && (
              <Banner variant="error" description={passwordError} />
            )}

            {passwordSuccess && (
              <Banner
                variant="success"
                description={content.password.dialog.success}
              />
            )}

            <DialogFooter>
              <Button
                size="lg"
                type="button"
                variant="outline"
                onClick={() => handlePasswordDialogOpenChange(false)}
                disabled={passwordLoading || passwordSuccess}
              >
                {content.password.dialog.cancel}
              </Button>

              <Button
                size="lg"
                className="mb-3 bg-sky-400"
                type="submit"
                disabled={passwordLoading || passwordSuccess}
              >
                {passwordLoading
                  ? content.password.dialog.updating
                  : passwordSuccess
                    ? content.password.dialog.updated
                    : content.password.dialog.update}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
